import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: email.trim() });
      // Always show the same success UI regardless of response — prevents
      // email enumeration even at the UI layer.
      setSubmitted(true);
    } catch (err) {
      // Even errors get the same success UI for the same reason; surface
      // only true 5xx to the user.
      if (err.response?.status >= 500) {
        toast.error('Something went wrong. Please try again in a moment.');
      } else {
        setSubmitted(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFCFB] to-[#F5F3F0] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-[#70170F] rounded-xl flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1A1A1A]">
            {submitted ? 'Check your email' : 'Forgot password?'}
          </CardTitle>
          <CardDescription className="text-gray-600">
            {submitted
              ? "If an account exists with that email, we've sent a password reset link. The link expires in 1 hour."
              : "Enter the email associated with your account and we'll send a reset link."}
          </CardDescription>
        </CardHeader>

        {submitted ? (
          <CardContent className="pt-6 pb-4">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="forgot-password-success">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-emerald-800">
                Reset email sent. Check your inbox (and spam folder) for a message from KEY-MADE.
                Didn't get it? Wait a minute, then try again with the exact email on file.
              </div>
            </div>
            <Link
              to="/login"
              className="mt-6 inline-flex items-center text-sm text-[#70170F] hover:underline"
              data-testid="forgot-password-back-to-login"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to sign in
            </Link>
          </CardContent>
        ) : (
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4 pt-6">
              <div>
                <Label htmlFor="email" className="flex items-center gap-2 mb-1.5">
                  <Mail className="w-4 h-4" /> Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="you@yourcompany.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-password-email-input"
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-3 pt-2">
              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-password-submit-btn"
                className="w-full bg-[#70170F] hover:bg-[#5a1209] text-white"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-[#70170F] hover:underline"
                data-testid="forgot-password-back-link"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to sign in
              </Link>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
}
